import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import type { Call } from '../../../src/shared/comms';
import { commsKeys, fetchCalls, fetchCall } from '../../api/comms';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { useRealtime } from '../../realtime/useRealtime';

function formatTimestamp(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function CallListItem({
  call,
  isActive,
  onClick,
}: {
  call: Call;
  isActive: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
        isActive
          ? 'border-slate-900 bg-slate-50'
          : call.isMissed
            ? 'border-red-200 bg-red-50 hover:border-red-300'
            : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`call-item-${call.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium text-slate-900">{call.fromE164}</span>
        <span className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize text-slate-600">
          {call.direction}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        {call.isMissed && (
          <span
            className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
            data-testid={`missed-badge-${call.id}`}
          >
            Missed
          </span>
        )}
        <span className="text-xs text-slate-500">
          {formatDuration(call.durationSeconds)} &middot; {formatTimestamp(call.startedAt)}
        </span>
      </div>
    </button>
  );
}

function CallDetailView({ call }: { call: Call }): React.JSX.Element {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="call-detail"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{call.fromE164}</h2>
        {call.isMissed && (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            Missed
          </span>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Direction</dt>
        <dd className="capitalize">{call.direction}</dd>
        <dt className="text-slate-500">Status</dt>
        <dd>{call.status}</dd>
        <dt className="text-slate-500">Duration</dt>
        <dd>{formatDuration(call.durationSeconds)}</dd>
        <dt className="text-slate-500">Provider</dt>
        <dd>{call.provider}</dd>
        <dt className="text-slate-500">Started</dt>
        <dd>{formatTimestamp(call.startedAt)}</dd>
        {call.endedAt !== null && (
          <>
            <dt className="text-slate-500">Ended</dt>
            <dd>{formatTimestamp(call.endedAt)}</dd>
          </>
        )}
        {call.contactId !== null && (
          <>
            <dt className="text-slate-500">Contact</dt>
            <dd>{call.contactId}</dd>
          </>
        )}
      </dl>

      {call.summary !== null && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-700">Summary</h3>
          <p className="mt-1 text-sm text-slate-600" data-testid="call-summary">
            {call.summary}
          </p>
        </div>
      )}

      {call.transcript !== null && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-700">Transcript</h3>
          <pre
            className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
            data-testid="call-transcript"
          >
            {call.transcript}
          </pre>
        </div>
      )}

      {call.qualification !== null && Object.keys(call.qualification).length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-700">Qualification</h3>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {Object.entries(call.qualification).map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="text-slate-500">{key}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

export function CallsPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();

  const [missedOnly, setMissedOnly] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const callsQuery = useQuery({
    queryKey: commsKeys.calls.list(slug!, missedOnly ?? undefined),
    queryFn: ({ signal }) => fetchCalls(slug!, missedOnly ? { missed: true } : {}, signal),
    enabled: slug !== undefined,
  });

  const callDetailQuery = useQuery({
    queryKey: commsKeys.calls.detail(slug!, selectedCallId!),
    queryFn: ({ signal }) => fetchCall(slug!, selectedCallId!, signal),
    enabled: slug !== undefined && selectedCallId !== null,
  });

  useRealtime(slug!);

  const selectedCall = callDetailQuery.data ?? null;

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Calls</h1>
      <p className="mt-1 text-sm text-slate-500">{membership.name}</p>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setMissedOnly(false);
            setSelectedCallId(null);
          }}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
            !missedOnly
              ? 'border-slate-900 bg-slate-900 text-white'
              : 'border-slate-300 text-slate-700 hover:bg-slate-100'
          }`}
        >
          All calls
        </button>
        <button
          type="button"
          onClick={() => {
            setMissedOnly(true);
            setSelectedCallId(null);
          }}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
            missedOnly
              ? 'border-red-700 bg-red-700 text-white'
              : 'border-slate-300 text-slate-700 hover:bg-slate-100'
          }`}
        >
          Missed
        </button>
      </div>

      {callsQuery.isError && (
        <div className="mt-4">
          <ErrorNotice error={callsQuery.error} />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="space-y-2">
          {callsQuery.isPending && <LoadingScreen />}

          {callsQuery.isSuccess && callsQuery.data.items.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500" data-testid="calls-empty">
                {missedOnly ? 'No missed calls.' : 'No calls yet.'}
              </p>
            </div>
          )}

          {callsQuery.isSuccess &&
            callsQuery.data.items.map((call) => (
              <CallListItem
                key={call.id}
                call={call}
                isActive={selectedCallId === call.id}
                onClick={() => setSelectedCallId(call.id)}
              />
            ))}
        </div>

        <div>
          {callDetailQuery.isPending && selectedCallId !== null && <LoadingScreen />}
          {callDetailQuery.isError && <ErrorNotice error={callDetailQuery.error} />}

          {selectedCall !== null ? (
            <CallDetailView call={selectedCall} />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
              <p className="text-sm text-slate-500">Select a call to view details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
